import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetPracticeAreaByIdQuery } from 'store/api/practice-areas';
import { PracticeArea, PracticeAreaForm, PracticeAreaType } from 'types/practice-areas';
import { useClassifiers } from 'utils/useClassifiers';
import { practiceAreaDtoToForm } from './practiceAreaFormMapping';
import PracticeAreaPageForm from './PracticeAreaPageForm';

const USED_CLASSIFIERS = ['PRACTICE_AREA_TYPE'];

/** The add form loaded with what a draft holds, read as the practice area's edit page reads it. */
const PracticeAreaDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: PracticeAreaForm; practiceArea: PracticeArea }>();
    const { data: practiceArea } = useGetPracticeAreaByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && practiceArea) {
        setDraft({ initialData: practiceAreaDtoToForm(practiceArea), practiceArea });
    }

    return draft && !isLoadingClassifiers ? (
        <PracticeAreaPageForm
            type="add"
            initialData={draft.initialData}
            classifiers={classifiers}
            entityCustomFields={draft.practiceArea.customFields}
        />
    ) : (
        <Loader />
    );
};

const PracticeAreaBlankAdd = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    // Derive initial form data from classifiers during render (no effect needed).
    const initialData = useMemo<PracticeAreaForm | undefined>(() => {
        if (!classifiers) return undefined;
        return {
            name: '',
            isActive: true,
            practiceAreaType: PracticeAreaType.PRACTICE_AREA,
            parent: null,
            practiceGroup: null,
            practiceTeams: [],
            isAmlApplicable: false,
            isExcludedFromAutomaticClosing: false,
            applyExclusionToSubareas: false,
            showInTimecards: true,
            showInCases: true,
            showInEmployees: true,
        };
    }, [classifiers]);

    if (isLoadingClassifiers || initialData === undefined) {
        return <Loader />;
    }

    return <PracticeAreaPageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The practice area create; its URL with `?draft=<id>` edits that draft instead of a blank practice area. */
const PracticeAreaPageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <PracticeAreaBlankAdd /> : <PracticeAreaDraftAdd key={draftId} draftId={draftId} />;
};

export default PracticeAreaPageAdd;
